import { CertificateSettingType, ICertificateStudentProcessed, ICertificatesStudent } from 'app_types/admin/certificateSettings/certificateSettings.types';
import React, { FC, useCallback, useMemo, useState } from 'react'
import CheckBoxComponent from 'app_components/commons/checkBox/containers/checkBoxComponent';

import './CertificateStudentItem.scss'
import TextComponent from 'app_components/commons/TextComponent/TextComponent';
import { Accordion } from 'react-bootstrap';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import TooltipComponent from 'app_components/commons/TooltipComponent/TooltipComponent';
import ChipButton from 'app_components/commons/ChipButton/ChipButton';
import { MdHorizontalRule } from 'react-icons/md';
import { ICourseSchedulingListItem } from 'app_types/admin/courseScheduling/courseSchedulingTypes';

const CERTIFICATION_TYPE_NAME = {
  [CertificateSettingType.ATTENDANCE]: "Asistencia",
  [CertificateSettingType.ATTENDANCE_APPROVAL]: "Asistencia y aprobación"
}

interface ICertificateStudentItemProps {
  certificate: ICertificatesStudent
  selectedCertifications: string[]
  onChangeSelections: (selected: string[]) => void
  courseScheduling: ICourseSchedulingListItem
}

enum GeneralCheckboxState {
  PARTIAL = 'partial',
  ALL = 'all',
  NONE = 'none',
  DISABLED = 'disabled',
}

const CertificateStudentItem: FC<ICertificateStudentItemProps> = ({
  certificate,
  onChangeSelections,
  selectedCertifications,
  courseScheduling
}) => {

  const certificateNamehasParentheses = (certificateName: string) => /^(\(.*\))/.test(certificateName.trim())

  const handleCheckboxIsDisabled = (certificateProcessed: ICertificateStudentProcessed) => {
    if (!certificateProcessed.approved) return true
    if (courseScheduling?.isMultiple && courseScheduling?.certificationStrategy === 'huella2') {
      const hasParentheses = certificateNamehasParentheses(certificateProcessed?.certificateName)
      if (!hasParentheses) return true
    }
    return false
  }
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined)
  const generalCheckboxState: GeneralCheckboxState = useMemo((): GeneralCheckboxState => {
    const totalActiveCertifications = certificate.certifications?.filter((item) => !handleCheckboxIsDisabled(item))?.length
    if (!totalActiveCertifications || totalActiveCertifications === 0) return GeneralCheckboxState.DISABLED
    if (!selectedCertifications?.length) return GeneralCheckboxState.NONE
    if (selectedCertifications?.length === totalActiveCertifications) {
      return GeneralCheckboxState.ALL
    }
    return GeneralCheckboxState.PARTIAL
  }, [selectedCertifications, certificate.certifications])

  const handleOnSelect = (e: any) => {
    setActiveKey(e)
  }

  const renderIcon = useCallback((selectedKey: string) => {
    if (activeKey === selectedKey) {
      return (<IoIosArrowUp size={18} />)
    }
    return (<IoIosArrowDown size={18} />)
  }, [activeKey])

  const getActiveColor = (certificateType: CertificateSettingType) => {
    if (certificateType === CertificateSettingType.ATTENDANCE) {
      return "#F3DAFF"
    } else {
      return undefined
    }
  }

  const handleClickCertificate = (selected: string) => {
    if (selectedCertifications.includes(selected)) {
      const newList = [...selectedCertifications]
      const idx = selectedCertifications.findIndex((item) => item === selected)
      newList.splice(idx, 1)
      onChangeSelections && onChangeSelections(newList)
    } else {
      onChangeSelections && onChangeSelections([...selectedCertifications, selected])
    }
  }

  const handleClickGeneralCheckbox = () => {
    if (generalCheckboxState === GeneralCheckboxState.NONE || generalCheckboxState === GeneralCheckboxState.PARTIAL) {
      const activeCertifications = certificate?.certifications?.filter((item) => !handleCheckboxIsDisabled(item))?.map((item) => item.certificateSettingId)
      onChangeSelections && onChangeSelections(activeCertifications ? activeCertifications : [])
    } else {
      onChangeSelections && onChangeSelections([])
    }
  }

  return (
    <div className="certificate-student-item--layout">
      <Accordion activeKey={activeKey} onSelect={handleOnSelect}>
        <Accordion.Toggle className='certificate-student-item--accordion-toggle' eventKey={`certificate-accordion-${certificate.userId}`}>
          <div className="certificate-student-item--header">
            <CheckBoxComponent
              checked={generalCheckboxState !== GeneralCheckboxState.NONE}
              disabled={generalCheckboxState === GeneralCheckboxState.DISABLED}
              icon={generalCheckboxState === GeneralCheckboxState.PARTIAL || generalCheckboxState === GeneralCheckboxState.DISABLED ? <MdHorizontalRule /> : undefined}
              onClick={generalCheckboxState === GeneralCheckboxState.DISABLED ?
                () => {} :
                handleClickGeneralCheckbox
              }
            />
            <TextComponent>
              {certificate.studentName}
            </TextComponent>
            <div className='certificate-student-item--toggle-icon'>
              {renderIcon(`certificate-accordion-${certificate.userId}`)}
            </div>
          </div>
        </Accordion.Toggle>
        <Accordion.Collapse eventKey={`certificate-accordion-${certificate.userId}`}>
          <div className='certificate-student-item--content'>
            <TextComponent color="black">
              <strong>Documento de identidad: </strong> {certificate.studentDocnumber}
            </TextComponent>
            <TextComponent color="black">
              <strong>Usuario: </strong> {certificate.studentUsername}
            </TextComponent>
            {certificate?.certifications?.map((certificateProcessed) => (
              <div className='certificate-student-item--certificate-processed' key={certificateProcessed.certificateSettingId}>
                <div className='certificate-student-item--certificate-processed-header'>
                  <CheckBoxComponent
                    disabled={handleCheckboxIsDisabled(certificateProcessed)}
                    checked={selectedCertifications.includes(certificateProcessed.certificateSettingId)}
                    onClick={!handleCheckboxIsDisabled(certificateProcessed) ?
                      () => handleClickCertificate(certificateProcessed.certificateSettingId) :
                      () => {}
                    }
                    icon={handleCheckboxIsDisabled(certificateProcessed) ? <MdHorizontalRule /> : undefined}
                  />
                  <TooltipComponent text={certificateProcessed.certificateName}>
                    <TextComponent color="black">
                      {certificateProcessed.certificateName}
                    </TextComponent>
                  </TooltipComponent>
                </div>
                {courseScheduling?.certificationStrategy === 'huella2' && handleCheckboxIsDisabled(certificateProcessed) && !certificateNamehasParentheses(certificateProcessed.certificateName) && (
                  <div className="certificate-student-item--certificate-processed-description">
                    <div className='certificate-student-item--certificate-processed-error'>
                      Error: El certificado tiene un formato incorrecto. Faltan los paréntesis al inicio del nombre.
                    </div>
                  </div>
                )}
                <div className="certificate-student-item--certificate-processed-description">
                  <ChipButton active activeColor={getActiveColor(certificateProcessed.certificateType)} padding="0px 10px" >
                    {CERTIFICATION_TYPE_NAME[certificateProcessed.certificateType]}
                  </ChipButton>
                </div>
              </div>
            ))}
          </div>
        </Accordion.Collapse>
      </Accordion>
    </div>
  )
}

export default CertificateStudentItem
